import React, { useState, useEffect } from 'react'
import { FiType, FiCheck } from 'react-icons/fi'

interface FontPickerProps {
  value: string
  onChange: (font: string) => void
  label: string
  category?: 'serif' | 'sans-serif' | 'display' | 'handwriting' | 'monospace'
}

const FontPicker: React.FC<FontPickerProps> = ({ value, onChange, label, category = 'sans-serif' }) => {
  const [fonts, setFonts] = useState<Array<{ family: string; category: string }>>([])
  const [loading, setLoading] = useState(true)

  // Popular Google Fonts by category
  const fontCategories = {
    'sans-serif': [
      'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Source Sans Pro',
      'Raleway', 'PT Sans', 'Nunito', 'Poppins', 'Ubuntu', 'Work Sans'
    ],
    'serif': [
      'Playfair Display', 'Lora', 'Merriweather', 'PT Serif', 'Crimson Text',
      'Libre Baskerville', 'Source Serif Pro', 'Crimson Pro', 'EB Garamond'
    ],
    'display': [
      'Oswald', 'Bebas Neue', 'Anton', 'Righteous', 'Bangers', 'Fredoka One',
      'Lobster', 'Pacifico', 'Dancing Script', 'Satisfy'
    ],
    'handwriting': [
      'Dancing Script', 'Pacifico', 'Satisfy', 'Kalam', 'Caveat', 'Shadows Into Light',
      'Permanent Marker', 'Indie Flower', 'Amatic SC'
    ],
    'monospace': [
      'Fira Code', 'Source Code Pro', 'Roboto Mono', 'JetBrains Mono', 'Inconsolata',
      'Cousine', 'PT Mono', 'Space Mono'
    ]
  }

  useEffect(() => {
    // Simulate loading Google Fonts
    setLoading(false)
    setFonts(fontCategories[category].map(font => ({ family: font, category })))
  }, [category])

  const handleFontSelect = (fontFamily: string) => {
    onChange(fontFamily)
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-neutral-700">
        {label}
      </label>
      
      {loading ? (
        <div className="text-center py-4">
          <div className="loading-spinner mx-auto mb-2"></div>
          <p className="text-sm text-neutral-500">Loading fonts...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-neutral-200 rounded-lg p-3">
          {fonts.map((font) => (
            <button
              key={font.family}
              type="button"
              onClick={() => handleFontSelect(font.family)}
              className={`flex items-center justify-between p-2 rounded-lg text-left transition-colors duration-200 ${
                value === font.family
                  ? 'bg-primary text-white'
                  : 'hover:bg-neutral-100 text-neutral-700'
              }`}
            >
              <div className="flex items-center space-x-3">
                <FiType size={16} />
                <span 
                  className="text-sm font-medium"
                  style={{ fontFamily: `"${font.family}", sans-serif` }}
                >
                  {font.family}
                </span>
              </div>
              {value === font.family && (
                <FiCheck size={16} className="text-white" />
              )}
            </button>
          ))}
        </div>
      )}
      
      {value && (
        <div className="mt-3 p-3 bg-neutral-50 rounded-lg">
          <p className="text-xs text-neutral-600 mb-2">Preview:</p>
          <p 
            className="text-lg"
            style={{ fontFamily: `"${value}", sans-serif` }}
          >
            The quick brown fox jumps over the lazy dog
          </p>
        </div>
      )}
    </div>
  )
}

export default FontPicker
